import { useCallback } from 'react';
import { MonitorPrompt } from '../../../../shared/models/monitor/MonitorPrompt';
import { PromptsData } from '../../../prompt/models/PromptsData';
import { MonitorPromptsKeywordsStatsResponse } from '../../../../shared/services/monitor/keywords/stats/MonitorPromptsKeywordsStatsResponse';
import { MonitorStatus } from '../../../../shared/types/prompt/MonitorStatusType';
import { MODEL_KEY_TO_CHAT_TYPES } from '../../../../shared/helpers/MonitorTypeHelpers';

interface UseProjectDataReturn {
  prepareAndSetPromptsData: (
    monitorPromptsData: MonitorPrompt[],
    keywordStats: MonitorPromptsKeywordsStatsResponse[],
    selectedChatType: string,
    allowedChatTypes?: Set<string>
  ) => PromptsData[];
}

export const useProjectData = (): UseProjectDataReturn => {
  const prepareAndSetPromptsData = useCallback(
    (
      monitorPromptsData: MonitorPrompt[],
      keywordStats: MonitorPromptsKeywordsStatsResponse[],
      selectedChatType: string,
      allowedChatTypes?: Set<string>
    ): PromptsData[] => {
      const sortedMonitorPrompts = [...monitorPromptsData].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );

      const monitorDataByChatType = new Map<
        string,
        Record<string, MonitorPromptsKeywordsStatsResponse>
      >();
      const monitorDataGeneral = new Map<string, MonitorPromptsKeywordsStatsResponse>();
      // Primary "ALL" average — respects the active model filter (allowedChatTypes), exactly as before.
      const monitorDataCombined = new Map<string, { totalPosition: number; count: number }>();
      // Average over EVERY model with data (incl. archival), ignoring the filter — the grey "all" value.
      const monitorDataCombinedAll = new Map<string, { totalPosition: number; count: number }>();

      const addToCombined = (
        map: Map<string, { totalPosition: number; count: number }>,
        key: string,
        position: unknown
      ) => {
        if (!map.has(key)) {
          map.set(key, { totalPosition: 0, count: 0 });
        }
        if (typeof position === 'number' && position > 0) {
          const combined = map.get(key)!;
          combined.totalPosition += position;
          combined.count += 1;
        }
      };

      keywordStats.forEach((data) => {
        const key = `${data.monitorId}_${data.keyword.replace(' ', '_')}`;

        if (data.chatType) {
          if (!monitorDataByChatType.has(key)) {
            monitorDataByChatType.set(key, {});
          }
          monitorDataByChatType.get(key)![data.chatType] = data;

          // "all" average: every model with data, regardless of the active model filter.
          addToCombined(monitorDataCombinedAll, key, data.averagePosition);

          // Only include models the user has access to in the (filter-respecting) "ALL" average
          const isAllowed = !allowedChatTypes || allowedChatTypes.has(data.chatType);

          if (isAllowed) {
            addToCombined(monitorDataCombined, key, data.averagePosition);
          }
        } else {
          monitorDataGeneral.set(key, data);
        }
      });

      const getMonitorData = (prompt: MonitorPrompt, chatType?: string) => {
        const key = `${prompt.id}_${prompt.keyword.replace(' ', '_')}`;
        if (chatType) {
          return monitorDataByChatType.get(key)?.[chatType] ?? null;
        }
        return monitorDataGeneral.get(key) ?? null;
      };

      // Per-model column data by model FAMILY, not by an exact chat_type string. A project may store a
      // model under any of its variants (e.g. GPT as 'GPT', 'GPT_BROWSER' or 'GPT_SEARCH'); an exact
      // 'GPT' lookup misses the browser/search variants and the whole column then collapses to '-'.
      // Resolve across all variants of the family (MODEL_KEY_TO_CHAT_TYPES); when several carry data
      // for one prompt, average them like the "ALL" combined value.
      const getModelFamilyData = (
        prompt: MonitorPrompt,
        modelKey: string
      ): Pick<
        MonitorPromptsKeywordsStatsResponse,
        'averagePosition' | 'averageShareOfVoice'
      > | null => {
        const key = `${prompt.id}_${prompt.keyword.replace(' ', '_')}`;
        const byType = monitorDataByChatType.get(key);
        if (!byType) return null;
        const entry = MODEL_KEY_TO_CHAT_TYPES.find((m) => m.key === modelKey);
        const variants = entry ? entry.chatTypes : [modelKey];
        const rows = variants
          .map((ct) => byType[ct])
          .filter((r): r is MonitorPromptsKeywordsStatsResponse => Boolean(r));
        if (rows.length === 0) return null;
        if (rows.length === 1) return rows[0];
        const mean = (nums: number[]) =>
          Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10;
        const positions = rows
          .map((r) => r.averagePosition)
          .filter((p): p is number => typeof p === 'number' && p > 0);
        // Share of Voice jest znormalizowany w obrębie POJEDYNCZEGO chat_type, więc przy kilku
        // wariantach rodziny (np. GPT + GPT_BROWSER) uśredniamy je tak jak pozycje — wynik zostaje
        // w skali jednego modelu.
        const sovs = rows
          .map((r) => r.averageShareOfVoice)
          .filter((s): s is number => typeof s === 'number' && s > 0);
        return {
          averagePosition: positions.length ? mean(positions) : null,
          averageShareOfVoice: sovs.length ? mean(sovs) : null,
        };
      };

      const averageOf = (
        map: Map<string, { totalPosition: number; count: number }>,
        prompt: MonitorPrompt
      ): number | '-' => {
        const key = `${prompt.id}_${prompt.keyword.replace(' ', '_')}`;
        const combinedData = map.get(key);
        return combinedData && combinedData.count > 0
          ? Math.round((combinedData.totalPosition / combinedData.count) * 10) / 10
          : '-';
      };

      const getCombinedMonitorData = (prompt: MonitorPrompt) => averageOf(monitorDataCombined, prompt);

      return sortedMonitorPrompts.map((prompt) => {
        const monitorData = getMonitorData(prompt);
        // Primary value = the original filter-respecting combined (unchanged from before).
        const combinedPosition = getCombinedMonitorData(prompt);
        // Grey "all" value = every model with data (incl. archival), regardless of the filter.
        const combinedPositionAll = averageOf(monitorDataCombinedAll, prompt);
        const searchVolume = prompt.searchVolume;
        const searchVolumeValue = searchVolume > 100 ? String(searchVolume) : '<100';

        const safeValue = (value: any) => (value && value !== 0 ? value : '-');

        return {
          id: prompt.id,
          prompt: prompt.prompt,
          status: prompt.status as MonitorStatus,
          searchKeyword: prompt.searchKeyword,
          searchVolume: searchVolumeValue,
          averagePosition:
            selectedChatType === 'ALL'
              ? combinedPosition && combinedPosition !== '-' && combinedPosition > 0
                ? combinedPosition
                : undefined
              : monitorData?.averagePosition ?? undefined,
          // Grey "all" value: ALL average over every model with data, incl. archival (NEW_STATS).
          averagePositionAll:
            selectedChatType === 'ALL' && combinedPositionAll !== '-' && combinedPositionAll > 0
              ? combinedPositionAll
              : undefined,
          averageGPTPosition: safeValue(getModelFamilyData(prompt, 'GPT')?.averagePosition),
          averageClaudePosition: safeValue(getModelFamilyData(prompt, 'CLAUDE')?.averagePosition),
          averageGeminiPosition: safeValue(getModelFamilyData(prompt, 'GEMINI')?.averagePosition),
          averageDeepSeekPosition: safeValue(getModelFamilyData(prompt, 'DEEP_SEEK')?.averagePosition),
          averagePerplexityPosition: safeValue(
            getModelFamilyData(prompt, 'PERPLEXITY')?.averagePosition
          ),
          averageCopilotPosition: safeValue(getModelFamilyData(prompt, 'COPILOT')?.averagePosition),
          averageGrokPosition: safeValue(getModelFamilyData(prompt, 'GROK')?.averagePosition),
          averageAiOverviewPosition: safeValue(
            getModelFamilyData(prompt, 'AI_OVERVIEW')?.averagePosition
          ),
          averageAiModePosition: safeValue(getModelFamilyData(prompt, 'AI_MODE')?.averagePosition),
          shareOfVoice: monitorData?.averageShareOfVoice ? monitorData.averageShareOfVoice + '%' : '-',
          averageGPTShareOfVoice: getModelFamilyData(prompt, 'GPT')?.averageShareOfVoice ?? '-',
          averageClaudeShareOfVoice: getModelFamilyData(prompt, 'CLAUDE')?.averageShareOfVoice ?? '-',
          averageGeminiShareOfVoice: getModelFamilyData(prompt, 'GEMINI')?.averageShareOfVoice ?? '-',
          averageDeepSeekShareOfVoice:
            getModelFamilyData(prompt, 'DEEP_SEEK')?.averageShareOfVoice ?? '-',
          averagePerplexityShareOfVoice:
            getModelFamilyData(prompt, 'PERPLEXITY')?.averageShareOfVoice ?? '-',
          averageCopilotShareOfVoice: getModelFamilyData(prompt, 'COPILOT')?.averageShareOfVoice ?? '-',
          averageGrokShareOfVoice: getModelFamilyData(prompt, 'GROK')?.averageShareOfVoice ?? '-',
          averageAiOverviewShareOfVoice:
            getModelFamilyData(prompt, 'AI_OVERVIEW')?.averageShareOfVoice ?? '-',
          averageAiModeShareOfVoice:
            getModelFamilyData(prompt, 'AI_MODE')?.averageShareOfVoice ?? '-',
          keyword: prompt.keyword,
          demoVersion: prompt.demoVersion,
          projectId: prompt.projectId,
          created: prompt.created,
          isRequiredAttention: false, // This will be set by the parent component
          hasAiOverview: prompt.hasAiOverview,
          promptGroupId: prompt.promptGroupId ?? null,
        } as PromptsData;
      });
    },
    []
  );

  return {
    prepareAndSetPromptsData,
  };
};
